import React, { useState, useEffect, useCallback } from "react";
import styles from "./Accordion.module.css";
import CardWrapper from "../CardWrapper";
import { Button } from "../Modal";
import {
  useStopTimerAutomatic,
  useStartTimerAutomatic,
  useGetTimer,
  useAddNote,
  useFetchNotes,
  useAllPatient,
} from "../../hooks/patients";
import { IPatientRes, INoteReq } from "../../hooks/patients/index.type";
import { TableButton } from "../../components/NewTable";
import ManualTimerModals, {
  ManualTimerModalState,
} from "../../pageComponents/Patients/ManualTimer";
import ManualTimerEntryModal, {
  ManualTimerEntryModalState,
} from "../../pageComponents/Patients/ManualTimerEntryModal";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { loginSliceValue } from "../../redux/reducers/loginSlice";

const buttonStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row-reverse",
  alignItems: "center",
  width: "90px",
  height: "35px",
  fontSize: "15px",
  fontWeight: 400,
  border: "1px solid #0F407A",
  borderRadius: "8px",
  background: "transparent",
  color: "#0F407A",
};

const disabledButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  opacity: 0.5,
  cursor: "not-allowed",
  border: "1px solid #ccc",
  color: "#ccc",
};

const iconStyle: React.CSSProperties = {
  alignItems: "center",
  height: "24px",
  width: "24px",
};

interface TimerCardProps {
  patientId?: string;
  clinicId?: string;
  isDisabled?: boolean;
  onTimerUpdate?: () => void;
  rtmStatus?: "Active" | "Inactive" | "Pending";
  patientData?: IPatientRes;
  setPatientData?: React.Dispatch<
    React.SetStateAction<IPatientRes | undefined>
  >;
  setShowAddNoteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setTriggeredBy: React.Dispatch<React.SetStateAction<string>>;
  onTimerStart?: () => void;
  onTimerStop?: () => void;
  getFloatingTimer?: () => void;
  setBillingSessionId?: React.Dispatch<React.SetStateAction<string>>;
  refreshNotes?: (() => Promise<void>) | null;
  billingCycleId?: string;
  patientName?: string;
  getPatientData?: () => Promise<void>;
}

const TimerCard: React.FC<TimerCardProps> = ({
  patientId = "67f9201baeaee53de8f8124a",
  clinicId = "67f6370f165bb75c18f85039",
  onTimerUpdate,
  rtmStatus,
  patientData,
  setPatientData,
  isDisabled = false,
  setShowAddNoteModal,
  setTriggeredBy,
  onTimerStart,
  onTimerStop,
  getFloatingTimer,
  setBillingSessionId,
  refreshNotes,
  billingCycleId = "",
  patientName,
  getPatientData,
}) => {
  const userDetails = useSelector(loginSliceValue);

  const [isRunning, setIsRunning] = useState(false);
  const [timerValue, setTimerValue] = useState("00:00:00");
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const [showManualTimerModal, setshowManualTimerModal] =
    React.useState<ManualTimerModalState>({
      status: false,
    });
  const [showManualTimerEntryModal, setShowManualTimerEntryModal] =
    React.useState<ManualTimerEntryModalState>({
      status: false,
    });

  const { getNotes, fetchingNotes, hasMore } = useFetchNotes({
    patientId: patientId,
  });
  const { addNote, addNoteLoading } = useAddNote({
    patientId: patientId,
  });

  const { getTimesLoading, timerData } = useGetTimer({
    patientId,
    clinicId,
  });

  const { StartTimer, StartTimerLoading } = useStartTimerAutomatic({
    patientId,
    clinicId,
  });

  const { StopTimer, StopTimerLoading } = useStopTimerAutomatic({
    patientId,
    clinicId,
  });

  const startButtonDisabled =
    isRunning ||
    StartTimerLoading ||
    getTimesLoading ||
    showManualTimerModal.status ||
    rtmStatus === "Inactive" ||
    rtmStatus === "Pending" ||
    isDisabled;

  const stopButtonDisabled =
    !isRunning ||
    StopTimerLoading ||
    getTimesLoading ||
    showManualTimerModal.status ||
    rtmStatus === "Inactive" ||
    rtmStatus === "Pending" ||
    isDisabled;

  const manualTimerDisabled =
    isRunning ||
    rtmStatus === "Inactive" ||
    rtmStatus === "Pending" ||
    isDisabled;

  useEffect(() => {
    try {
      if (timerData?.isAlreadyStarted && timerData?.data) {
        const startTimeValue =
          timerData.data.startTimerTimestampUtc ||
          timerData.data.startTimerLocal;
        if (!startTimeValue) {
          console.error("No valid start time found in timer data");
          return;
        }

        const utcStartTime = new Date(startTimeValue);
        if (isNaN(utcStartTime.getTime())) {
          throw new Error(`Invalid start time format: ${startTimeValue}`);
        }

        setIsRunning(true);
        setStartTime(utcStartTime);

        const now = Date.now();
        const elapsed = Math.floor((now - utcStartTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      } else {
        setIsRunning(false);
        setStartTime(null);
        setElapsedTime(0);
      }
    } catch (error) {
      console.error("Error initializing timer:", error);
      setIsRunning(false);
      setStartTime(null);
      setElapsedTime(0);
    }
  }, [timerData]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning && startTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const start = startTime.getTime();
        const elapsed = Math.floor((now - start) / 1000);
        setElapsedTime(elapsed);

        const hours = Math.floor(elapsed / 3600);
        const minutes = Math.floor((elapsed % 3600) / 60);
        const seconds = elapsed % 60;

        setTimerValue(
          `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
        );
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, startTime]);

  const formatCurrentDate = () => {
    const now = new Date();
    return `${(now.getMonth() + 1).toString().padStart(2, "0")}/${now
      .getDate()
      .toString()
      .padStart(2, "0")}/${now.getFullYear()} ${now
      .getHours()
      .toString()
      .padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now
      .getSeconds()
      .toString()
      .padStart(2, "0")}`;
  };

  const handleStartTimer = async () => {
    if (startButtonDisabled) return;
    try {
      const formattedDate = formatCurrentDate();
      const formData = {
        startTimer: formattedDate,
      };
      const response = await StartTimer(formData);

      if (response?.status === "success") {
        toast.success("Timer has been started");
        setIsRunning(true);
        setElapsedTime(0);

        if (onTimerStart) {
          onTimerStart();
        }
        const startTimeValue =
          response?.data?.startTimerTimestampUtc || new Date().toISOString();
        const startTime = new Date(startTimeValue);

        if (isNaN(startTime.getTime())) {
          console.warn("Invalid start time from server, using current time");
          setStartTime(new Date());
        } else {
          setStartTime(startTime);
        }

        if (setPatientData && response.patientLatestData) {
          const updatedPatient = response.patientLatestData;
          setPatientData((prev) => {
            if (!prev) return undefined;
            const newPatientData = prev.data.data.map((p) => {
              if (p._id === updatedPatient._id) {
                return updatedPatient;
              }
              return p;
            });
            return {
              ...prev,
              data: {
                ...prev.data,
                data: newPatientData,
              },
            };
          });
        }
        if (getFloatingTimer) {
          getFloatingTimer();
        }
        if (onTimerUpdate) {
          onTimerUpdate();
        }
      }
    } catch (error) {
      console.error("Error starting timer:", error);
      toast.error("Failed to start timer");
    }
  };

  const handleStopTimer = async () => {
    if (stopButtonDisabled) return;

    try {
      const formattedDate = formatCurrentDate();
      const formData = {
        endTimer: formattedDate,
      };

      const response = await StopTimer(formData);

      if (response?.status === "success") {
        setIsRunning(false);
        setTimerValue("00:00:00");
        setStartTime(null);
        if (onTimerStop) {
          onTimerStop();
        }

        if (setPatientData && response.data) {
          const updatedPatient = response.data;
          setPatientData((prev) => {
            if (!prev) return undefined;
            const newPatientData = prev.data.data.map((p) => {
              if (p._id === updatedPatient._id) {
                return updatedPatient;
              }
              return p;
            });
            return {
              ...prev,
              data: {
                ...prev.data,
                data: newPatientData,
              },
            };
          });
        }
        if (onTimerUpdate) {
          onTimerUpdate();
        }

        // Get billingSessionId from response and automatically add note
        const billingSessionId = (response as any)?.billingSessionId;
        if (billingSessionId) {
          setBillingSessionId?.(billingSessionId);
        }

        const noteResult = await handleAddTimerNote(billingSessionId);

        if (noteResult?.status === "success") {
          setTriggeredBy("stop");
        }
      }
    } catch (error) {
      console.error("Error stopping timer:", error);
      toast.error("Failed to stop timer");
    }
  };

  const handleAddTimerNote = async (sessionId?: string) => {
    try {
      if (!patientId) {
        throw new Error("User not authenticated");
      }

      const noteContent =
        "Timer Summary - Software-verified timestamp confirms recorded activity.";
      const richTextContent =
        "<p>Timer Summary - Software-verified timestamp confirms recorded activity.</p>";

      const noteData: INoteReq = {
        noteCategory: "timer-summary",
        note: noteContent,
        richText: richTextContent,
        type: "general-note" as any,
      };

      if (noteData.noteCategory === "timer-summary" && sessionId) {
        noteData.billingSessionId = sessionId;
      }

      const response = await addNote(noteData);

      if (response?.status === "success") {
        await getNotes();
        return response;
      } else {
        const errorMessage = (response as any)?.message || "Failed to add note";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save note";
      toast.error(errorMessage);
      console.error("Error adding timer note:", error);
      return { status: "fail" as const };
    }
  };

  const openManualTimerModal = () => {
    if (manualTimerDisabled) return;
    setshowManualTimerModal({ status: true });
  };

  const handleManualTimerSuccess = useCallback(async () => {
    try {
      if (onTimerUpdate) {
        onTimerUpdate();
      }
    } catch (error) {
      console.error("Error refreshing data after manual timer entry:", error);
    }
  }, [onTimerUpdate]);

  const closeManualTimerModal = useCallback(() => {
    setshowManualTimerModal({ status: false });
  }, []);

  // const openManualTimerEntryModal = () => {
  //   if (manualTimerDisabled) return;
  //   setShowManualTimerEntryModal({ status: true });
  // };

  const closeManualTimerEntryModal = useCallback(() => {
    setShowManualTimerEntryModal({ status: false });
  }, []);

  return (
    <>
      <CardWrapper>
        <div className="displayflex alignCenter justrifySpaceBetween mb-half">
          <p className={styles.title}>Start Encounter</p>
          {userDetails.userDetails.data?.user.role !== "clinician" && (
            <div className="displayflex gap-1">
              {/* <TableButton
                icon="add"
                tooltip="Add Minutes"
                iconStyle={{
                  height: "1.3rem",
                  marginBottom: "0.3rem",
                  width: "1.3rem",
                }}
                onClick={openManualTimerEntryModal}
                disabled={manualTimerDisabled}
              /> */}
              {/* <TableButton
                icon="update"
                tooltip="Manual Timer"
                onClick={openManualTimerModal}
                disabled={manualTimerDisabled}
              /> */}
            </div>
          )}
        </div>

        <div className={styles.timerControls}>
          <div className={styles.timerButtons}>
            <Button
              label="Start"
              icon="icon-start-timer"
              span={false}
              submitButtonStyle={
                startButtonDisabled ? disabledButtonStyle : buttonStyle
              }
              iconStyle={iconStyle}
              onClick={handleStartTimer}
              disabled={startButtonDisabled}
            />
            <Button
              label="Stop"
              icon="icon-stop-timer"
              span={false}
              submitButtonStyle={
                stopButtonDisabled ? disabledButtonStyle : buttonStyle
              }
              iconStyle={iconStyle}
              onClick={handleStopTimer}
              // disabled={stopButtonDisabled}
            />
          </div>
          <div className={styles.timerBox}>{timerValue}</div>
        </div>
      </CardWrapper>

      <ManualTimerModals
        ManualTimerModal={showManualTimerModal}
        onCloseModal={closeManualTimerModal}
        patientId={patientId}
        clinicId={clinicId}
        billingCycleId={billingCycleId}
        onSuccess={handleManualTimerSuccess}
        patientData={patientData}
        setPatientData={setPatientData}
        patientName={patientName}
        getPatientData={getPatientData}
      />
      <ManualTimerEntryModal
        ManualTimerModal={showManualTimerEntryModal}
        onCloseModal={closeManualTimerEntryModal}
        patientId={patientId}
        clinicId={clinicId}
        onSuccess={handleManualTimerSuccess}
        patientData={patientData}
        setPatientData={setPatientData}
        patientName={patientName}
      />
    </>
  );
};

export default TimerCard;
